<template>
  <div class="page-container-v2">
    <a-spin :spinning="loading" tip="加载中...">
      <page-header
        :title="'自动回复设置管理'"
        :sub-title="'管理微信公众号的自动回复功能'"
      >
      </page-header>

      <div class="main-container">
        <div class="main-container__left">
          <a-card :bordered="false" class="auto-replay-card">
            <div class="setting-section">
              <div class="setting-area">
                <a-tabs v-model:activeKey="activeTabKey" @change="handleTabChange">
                  <a-tab-pane key="welcome" tab="欢迎语">
                    <!-- AC-FE-SELECTOR-001: 소유 공중계정이 2개 이상이면 셀렉터 표시 (라벨=displayName/accountType) -->
                    <div v-if="ownedLoaded && ownedGzhList.length > 1" class="gzh-selector">
                      <a-select
                        v-model:value="selectedGzhName"
                        style="min-width: 260px;"
                        @change="onGzhChange"
                      >
                        <a-select-option
                          v-for="acc in ownedGzhList"
                          :key="acc.gzhName"
                          :value="acc.gzhName"
                        >
                          {{ acc.displayName }}（{{ acc.accountType }}）
                        </a-select-option>
                      </a-select>
                    </div>

                    <!-- AC-FE-EMPTY-001: 소유 공중계정이 0개면 API 오류 없이 빈 상태 안내만 표시 -->
                    <div v-else-if="ownedLoaded && ownedGzhList.length === 0" class="gzh-empty-notice">
                      <a-empty description="관리 가능한 공중계정이 없습니다" />
                    </div>

                    <welcome-message
                      v-if="ownedLoaded && ownedGzhList.length > 0"
                      v-model:content="autoReplayContent"
                      :loading="loading"
                      :saving="savingData"
                      :error-msg="errorMsg"
                      :success-msg="successMsg"
                      :original-content="originalContent"
                      :gzh-name="selectedGzhName"
                      :miniprogram-api="apis && apis.miniprogram"
                      @save="saveAutoReplay"
                      @reset="resetForm"
                    />
                  </a-tab-pane>
                  <a-tab-pane key="keyword" tab="关键词回复">
                    <!--
                      SPEC-CHINA-GZH-CONSOLIDATE-001 M5(REQ-FE-CUTOVER-001, INV-6): 关键词回复 탭 단일 라우팅.
                      Dentium 레거시-라우팅 예외(KeywordReplyMessage, $apiInstance)를 제거 — dyh 웹훅 v1→v2
                      컷오버와 동일 윈도에 배포되어 admin 쓰기 스토어와 CF 읽기 스토어(china-inapi)가 동시 전환된다.
                      모든 테넌트(dentium 포함)가 china-inapi 파티션 스토어 UI(TenantKeywordManagement)를 쓴다.
                        - 소유 공중계정 0개 → 오류 없이 빈 상태만 표시(欢迎语 탭과 동일 패턴).
                    -->
                    <div v-if="ownedLoaded && ownedGzhList.length === 0" class="gzh-empty-notice">
                      <a-empty description="没有可管理的公众号" />
                    </div>

                    <tenant-keyword-management
                      v-else-if="ownedLoaded"
                      :owned-gzh-list="ownedGzhList"
                      v-model:selected-keyword-reply="selectedKeywordReply"
                      v-model:selected-gzh-name="keywordSelectedGzhName"
                    />
                  </a-tab-pane>
                </a-tabs>
              </div>
            </div>
          </a-card>
        </div>
        <div class="main-container__right">
          <mobile-emulator
            :content="activeTabKey === 'welcome' ? autoReplayContent : selectedKeywordReply"
            :user-message="activeTabKey === 'keyword' && selectedKeywordReply ? selectedKeywordReply.keyword : ''"
            :chat-title="emulatorChatTitle"
            :logo-text="emulatorLogoText"
            :logo-sub-text="emulatorLogoSubText"
          />
        </div>
      </div>
    </a-spin>
  </div>
</template>

<script>
import PageHeader from '@/views/components/PageHeader.vue';
import MobileEmulator from './components/MobileEmulator.vue';
import WelcomeMessage from './components/FollowWelcomeMessage.vue';
import TenantKeywordManagement from './components/keyword/TenantKeywordManagement.vue';
import gzhApis from './api';
import { oneBtnDialog } from '@/utils/utils';

// SPEC-CHINA-GZH-CONSOLIDATE-001 M5: Dentium 레거시 예외(DENTIUM_GZH_NAMES/isDentiumTenant) 제거 —
// 关键词回复 탭은 전 테넌트가 china-inapi 파티션 스토어를 사용한다(테넌트명 하드코딩 없음).

export default {
  name: 'AutoReplayManagement',
  components: {
    PageHeader,
    MobileEmulator,
    WelcomeMessage,
    TenantKeywordManagement
  },
  data() {
    return {
      loading: false,
      savingData: false,
      errorMsg: '',
      successMsg: '',
      apis: null,
      autoReplayContent: '',
      originalContent: '',
      activeTabKey: 'welcome',
      selectedKeywordReply: null,
      welcomeLoading: false,
      // 欢迎语(팔로우 환영 메시지) 탭 — china-inapi 테넌트 스코프 소유 공중계정 상태
      // SPEC-CHINA-GZH-AUTOREPLY-TENANT-001 M3(AC-FE-SWITCH-001/AC-FE-SELECTOR-001~003/AC-FE-EMPTY-001)
      ownedGzhList: [],
      ownedLoaded: false,
      selectedGzhName: '',
      // 关键词回复 탭(TenantKeywordManagement, 비-Dentium 테넌트)이 v-model:selected-gzh-name으로
      // 끌어올리는 선택 공중계정 — 欢迎语 탭의 selectedGzhName과 독립(탭별로 다른 GZH를 볼 수 있음).
      keywordSelectedGzhName: ''
    };
  },
  computed: {
    selectedGzhInfo() {
      return this.ownedGzhList.find((acc) => acc.gzhName === this.selectedGzhName) || null;
    },
    selectedGzhDisplayName() {
      return this.selectedGzhInfo ? this.selectedGzhInfo.displayName : '';
    },
    // 미리보기 상단 아바타(로고박스) 텍스트 — 선택된 공중계정을 따라간다(하드코딩 "登腾" 제거).
    // 미선택 시 빈 문자열을 넘겨 MobileEmulator 기본값(登腾/Dentium)으로 폴백한다.
    selectedGzhLogoText() {
      return this.selectedGzhDisplayName ? this.selectedGzhDisplayName.slice(0, 2) : '';
    },
    selectedGzhAccountType() {
      return this.selectedGzhInfo ? this.selectedGzhInfo.accountType || '' : '';
    },
    // 关键词回复 탭(비-Dentium, TenantKeywordManagement)에서 선택된 공중계정 정보 — 欢迎语 탭의
    // selectedGzhInfo와 동일 패턴이나 keywordSelectedGzhName(탭 전용 v-model)을 기준으로 한다.
    keywordSelectedGzhInfo() {
      return this.ownedGzhList.find((acc) => acc.gzhName === this.keywordSelectedGzhName) || null;
    },
    keywordSelectedGzhDisplayName() {
      return this.keywordSelectedGzhInfo ? this.keywordSelectedGzhInfo.displayName : '';
    },
    keywordSelectedGzhLogoText() {
      return this.keywordSelectedGzhDisplayName ? this.keywordSelectedGzhDisplayName.slice(0, 2) : '';
    },
    keywordSelectedGzhAccountType() {
      return this.keywordSelectedGzhInfo ? this.keywordSelectedGzhInfo.accountType || '' : '';
    },
    // MobileEmulator 헤더/아바타 바인딩 — 欢迎语 탭은 기존 그대로, 关键词回复 탭은 그 탭에서
    // 실제 선택된 GZH 정보를 반영한다(전 테넌트 공통, SPEC-CHINA-GZH-CONSOLIDATE-001 M5).
    emulatorChatTitle() {
      if (this.activeTabKey === 'welcome') return this.selectedGzhDisplayName;
      if (this.activeTabKey === 'keyword') return this.keywordSelectedGzhDisplayName;
      return '';
    },
    emulatorLogoText() {
      if (this.activeTabKey === 'welcome') return this.selectedGzhLogoText;
      if (this.activeTabKey === 'keyword') return this.keywordSelectedGzhLogoText;
      return '';
    },
    emulatorLogoSubText() {
      if (this.activeTabKey === 'welcome') return this.selectedGzhAccountType;
      if (this.activeTabKey === 'keyword') return this.keywordSelectedGzhAccountType;
      return '';
    }
  },
  created() {
    this.apis = gzhApis();

    // URL 쿼리 파라미터(?tab=welcome|keyword)로 탭 초기화 — 새로고침/딥링크 시 탭 유지
    const queryTab = this.$route.query.tab;
    if (queryTab === 'welcome' || queryTab === 'keyword') {
      this.activeTabKey = queryTab;
    }

    this.fetchOwnedGzh();
  },
  methods: {
    // 欢迎语 탭 진입 시 소유 공중계정 목록 조회 (AC-TENANT-001).
    // 0개면 에러 없이 빈 상태만 표시(AC-FE-EMPTY-001), 1개면 자동 선택+셀렉터 숨김(AC-FE-SELECTOR-002),
    // 2개 이상이면 셀렉터를 노출한다(AC-FE-SELECTOR-001).
    async fetchOwnedGzh() {
      try {
        this.loading = true;
        this.errorMsg = '';

        const response = await this.apis.welcomeMessage.getOwned();
        const owned = response.data?.owned || [];
        this.ownedGzhList = owned;
        this.ownedLoaded = true;

        if (owned.length === 0) {
          // 관리 가능한 공중계정 없음 — 오류 토스트 없이 빈 상태만 노출
          return;
        }

        // 1개면 자동 선택, 2개 이상이면 첫 번째를 기본값으로 선택(셀렉터로 전환 가능)
        this.selectedGzhName = owned[0].gzhName;
        await this.fetchWelcome(this.selectedGzhName);
      } catch (error) {
        console.error('Error fetching owned gzh list:', error);
        this.ownedLoaded = true;
        this.errorMsg = '获取可管理公众号列表失败: ' + (error.response?.data?.message || error.message);
      } finally {
        this.loading = false;
      }
    },

    // 선택된 공중계정의 欢迎语 조회 (AC-TENANT-002/AC-AUTOREPLY-001)
    async fetchWelcome(name) {
      if (!name) return;
      try {
        this.loading = true;
        this.errorMsg = '';

        const response = await this.apis.welcomeMessage.getWelcome(name);
        this.autoReplayContent = response.data?.gzhFollowAutoReplay || '';
        this.originalContent = this.autoReplayContent;
      } catch (error) {
        console.error('Error fetching welcome message:', error);
        this.errorMsg = '获取公众号信息失败: ' + (error.response?.data?.message || error.message);
      } finally {
        this.loading = false;
      }
    },

    // 셀렉터에서 공중계정을 변경하면 그 공중계정의 欢迎语로 다시 조회한다.
    async onGzhChange(name) {
      await this.fetchWelcome(name);
    },

    // 欢迎语 저장 (AC-AUTOREPLY-002/AC-AUTOREPLY-005) — chinaInapi PUT welcome?name=<selected>
    async saveAutoReplay() {
      if (!this.selectedGzhName) return;
      try {
        this.savingData = true;
        this.errorMsg = '';
        this.successMsg = '';

        const response = await this.apis.welcomeMessage.updateWelcome(
          this.selectedGzhName,
          this.autoReplayContent
        );

        if (response.data && response.data.ok) {
          this.successMsg = '自动回复设置已成功保存';
          this.originalContent = this.autoReplayContent;

          // 화면 상태를 유지해야 하므로 reload 트리거(3번째 인자 true)는 사용하지 않는다.
          await oneBtnDialog('保存成功', '自动回复设置已成功更新', false);
        } else {
          throw new Error(response.data?.message || '保存失败');
        }
      } catch (error) {
        console.error('Error saving welcome message:', error);
        this.errorMsg = '保存自动回复设置失败: ' + (error.response?.data?.message || error.message);

        await oneBtnDialog('保存失败', '保存自动回复设置时出错，请稍后重试', false);
      } finally {
        this.savingData = false;
      }
    },

    resetForm() {
      this.autoReplayContent = this.originalContent;
    },

    handleTabChange(key) {
      this.activeTabKey = key;

      // 탭 상태를 URL 쿼리에 동기화(replace로 히스토리 오염 방지). 같은 쿼리면 스킵.
      if (this.$route.query.tab === key) return;
      this.$router.replace({
        query: { ...this.$route.query, tab: key }
      }).catch((err) => {
        // 동일 쿼리로의 중복 네비게이션 등 라우터 에러는 무시
        if (err && err.name !== 'NavigationDuplicated') {
          console.error('Error updating tab query:', err);
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.page-container-v2 {
  padding: 16px;
  max-width: 1200px;
  margin: 0 auto;
}

.main-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 20px;
  gap: 24px;
  
  &__left {
    width: 60%;
    max-width: 700px;
  }
  
  &__right {
    width: 375px;
  }
}

.auto-replay-card {
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  background-color: #fff;
  
  :deep(.ant-tabs-nav) {
    margin-bottom: 16px;
  }
}

.setting-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.setting-area {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.preview-area {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

@media (max-width: 1200px) {
  .main-container {
    flex-direction: column;
    align-items: center;
    
    &__left {
      width: 100%;
      margin-bottom: 24px;
    }
    
    &__right {
      width: 375px;
      margin: 0 auto;
    }
  }
}
</style>
